import { css } from 'aphrodite';
import * as React from 'react';
import type { IAccount } from '../../../extViewmodels';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import {
	type SupportAgentTicketCountMap,
	UNASSIGNED_COUNT_KEY,
} from '../../../queries/Support/useSupportAgentTicketCounts';
import type { InboxNavItem } from '../../../queries/Support/useSupportInboxNav';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import type { ISupportAgentConfig } from '../../../queries/Support/useSupportTeam';
import { getSupportTeamPresentation } from '../../../queries/Support/useSupportTeam';
import {
	type BulkTicketUpdate,
	useBulkUpdateTicketsMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { DataGrid, DataGridRow, DataGridRowItem, DataGridRows } from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import {
	countActiveInboxFilters,
	agentTicketCountTitle,
	formatAgentTicketCount,
	type InboxFilters,
	prepareInboxFilterChoices,
} from './inboxFilters';
import { ArchiveGlyph, CheckGlyph, PeopleGlyph, PersonGlyph, SpamGlyph, UnassignedGlyph } from './inboxIcons';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';
import { getTicketAssigneeName } from './supportTicketPresentation';
import { getTicketTableColumns, type TicketTableColumnKey } from './ticketTableColumns';
import type { TicketListSource } from './TicketListPane';
import { TicketPagination } from './TicketPagination';
import { TicketSearchBar } from './TicketSearchBar';
import { type EditMenuOption, TicketBulkActionsBar } from './TicketTableActions';
import { TicketTableHeader } from './TicketTableHeader';
import { TicketTableRow } from './TicketTableRow';
import { AccountScopePicker, SearchCriteriaBar } from './TicketTableScopeControls';
import { TICKET_FIELD_SHORTCUTS, type TicketFieldShortcut, useTicketShortcuts } from './useTicketShortcuts';

const VIEW_ICONS: Record<string, React.FC<{ className?: string; size?: number }>> = {
	unassigned: UnassignedGlyph,
	mine: PersonGlyph,
	assigned: PeopleGlyph,
	closed: CheckGlyph,
	spam: SpamGlyph,
	archive: ArchiveGlyph,
};

interface TicketTableViewProps {
	list: TicketListSource;
	navItems: InboxNavItem[];
	activeFilter: string;
	onSelectFilter: (key: string) => void;
	isSearching: boolean;
	searchValue: string;
	onSearchChange: (value: string) => void;
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	onClearFilters: () => void;
	categories: ISupportCategory[];
	teamMembers: ISupportAgentConfig[];
	/** Open-ticket load per agent, shown beside each name in the assignee pickers. */
	agentTicketCounts?: SupportAgentTicketCountMap;
	categoryMap: Record<string, { name: string; color?: string }>;
	showAccountColumn: boolean;
	accountId?: string;
	accountName?: string;
	onSelectAccount?: (account: IAccount) => void;
	onClearAccount?: () => void;
	onOpenTicket: (id: string) => void;
}

function getColumnStyle(column: TicketTableColumnKey) {
	switch (column) {
		case 'select':
			return ts.colCheckbox;
		case 'number':
			return ts.colNumber;
		case 'subject':
			return ts.colSubject;
		case 'contact':
			return ts.colContact;
		case 'account':
			return ts.colAccount;
		case 'status':
			return ts.colStatus;
		case 'priority':
			return ts.colPriority;
		case 'category':
			return ts.colCategory;
		case 'assignee':
			return ts.colAssignee;
		case 'lastUpdate':
			return ts.colTime;
		default:
			throw new Error(`Unsupported ticket table column: ${column}`);
	}
}

export const TicketTableView: React.FC<TicketTableViewProps> = ({
	list,
	navItems,
	activeFilter,
	onSelectFilter,
	isSearching,
	searchValue,
	onSearchChange,
	filters,
	onFiltersChange,
	onClearFilters,
	categories,
	teamMembers,
	agentTicketCounts,
	categoryMap,
	showAccountColumn,
	accountId,
	accountName,
	onSelectAccount,
	onClearAccount,
	onOpenTicket,
}) => {
	const { tickets, isLoading, isError, page, totalPages, onSelectPage } = list;
	const userSession = useAdminUserSession();
	const currentAccountId = userSession.account?.id;
	const columns = getTicketTableColumns(showAccountColumn);
	const activeFilterCount = countActiveInboxFilters(filters);
	const filterChoices = React.useMemo(
		() => prepareInboxFilterChoices(categories, teamMembers, agentTicketCounts),
		[agentTicketCounts, categories, teamMembers]
	);
	const teamPresentation = React.useMemo(
		() => getSupportTeamPresentation(teamMembers, currentAccountId),
		[currentAccountId, teamMembers]
	);
	const assigneeEditOptions: ReadonlyArray<EditMenuOption<string>> = React.useMemo(
		() => [
			{
				value: '',
				label: 'Unassigned',
				hint: formatAgentTicketCount(agentTicketCounts, UNASSIGNED_COUNT_KEY),
				hintTitle: agentTicketCountTitle(UNASSIGNED_COUNT_KEY),
			},
			...teamMembers.map(member => ({
				value: member.agentId,
				label: member.agentName || 'Unnamed agent',
				hint: formatAgentTicketCount(agentTicketCounts, member.agentId),
				hintTitle: agentTicketCountTitle(member.agentId),
			})),
		],
		[agentTicketCounts, teamMembers]
	);
	const categoryEditOptions: ReadonlyArray<EditMenuOption<string>> = React.useMemo(
		() => [
			{ value: '', label: 'No category' },
			...filterChoices.activeCategories.map(category => ({ value: category.id, label: category.name })),
		],
		[filterChoices.activeCategories]
	);
	// The caller's reachable desks — the same set the move endpoint accepts as a destination.
	const { data: mailboxes } = useSupportMailboxListQuery();
	const mailboxEditOptions: ReadonlyArray<EditMenuOption<string>> = React.useMemo(
		() =>
			(mailboxes ?? [])
				.filter(mailbox => mailbox.isActive)
				.map(mailbox => ({ value: mailbox.id, label: mailbox.name })),
		[mailboxes]
	);

	const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(() => new Set());
	const [actionError, setActionError] = React.useState<string | null>(null);
	const [shortcutMenu, setShortcutMenu] = React.useState<TicketFieldShortcut | null>(null);
	const bulkUpdate = useBulkUpdateTicketsMutation();

	React.useEffect(() => {
		setSelectedIds(new Set());
	}, [page, activeFilter, filters, isSearching]);

	const selectedTickets = tickets.filter(ticket => selectedIds.has(ticket.id));
	const allSelected = tickets.length > 0 && selectedTickets.length === tickets.length;
	const hasSelection = selectedTickets.length > 0;

	// The shortcuts act on the selection, so they only listen while there is one — and the raised menu
	// closes with it, rather than staying open over tickets it can no longer change.
	useTicketShortcuts({
		enabled: hasSelection && !bulkUpdate.isPending,
		shortcuts: TICKET_FIELD_SHORTCUTS,
		onShortcut: menu => setShortcutMenu(menu),
	});
	React.useEffect(() => {
		if (!hasSelection) setShortcutMenu(null);
	}, [hasSelection]);

	const toggleTicket = (id: string) =>
		setSelectedIds(previous => {
			const next = new Set(previous);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(tickets.map(ticket => ticket.id)));

	const applyBulkUpdate = (update: BulkTicketUpdate) => {
		bulkUpdate.mutate(
			{ ticketIds: selectedTickets.map(ticket => ticket.id), update },
			{
				onSuccess: () => {
					setActionError(null);
					setSelectedIds(new Set());
				},
				onError: (error: unknown) => setActionError(getApiErrorMessage(error, 'Failed to update tickets.')),
			}
		);
	};

	const stateRow = (content: React.ReactNode) => (
		<DataGridRow>
			<DataGridRowItem colSpan={columns.length}>
				<div className={css(ts.tableState)}>{content}</div>
			</DataGridRowItem>
		</DataGridRow>
	);

	return (
		<section className={css(ts.tablePane)}>
			<div className={css(ts.tableHeaderArea)}>
				<nav aria-label='Inbox views' className={css(ts.viewsRow)}>
					{!accountId &&
						!isSearching &&
						navItems.map(item => {
							const Icon = VIEW_ICONS[item.key] ?? PersonGlyph;
							const active = activeFilter === item.key;
							return (
								<Button
									key={item.key}
									type='button'
									variant='ghost'
									styles={active ? [ts.viewTab, ts.viewTabActive] : [ts.viewTab]}
									aria-current={active ? 'page' : undefined}
									onClick={() => onSelectFilter(item.key)}
								>
									<Icon className={css(ts.viewTabIcon)} size={16} />
									<span>{item.label}</span>
									{item.count > 0 && <span className={css(ts.viewTabCount)}>{item.count}</span>}
								</Button>
							);
						})}
					{accountId && onSelectAccount && onClearAccount && (
						<AccountScopePicker
							accountId={accountId}
							accountName={accountName}
							onSelectAccount={onSelectAccount}
							onClearAccount={onClearAccount}
						/>
					)}
					<TicketSearchBar
						value={searchValue}
						onChange={onSearchChange}
						filters={filters}
						onFiltersChange={onFiltersChange}
						categoryOptions={filterChoices.categoryOptions}
						assigneeOptions={filterChoices.assigneeOptions}
						allowSort={!isSearching}
						onClearAll={onClearFilters}
						styles={[ts.tableSearchBar]}
						panelStyles={[ts.tableFilterPanel]}
					/>
					{activeFilterCount > 0 && (
						<Button type='button' variant='ghost' styles={[ts.clearFiltersBtn]} onClick={onClearFilters}>
							Clear filters ({activeFilterCount})
						</Button>
					)}
				</nav>

				{hasSelection && (
					<TicketBulkActionsBar
						selectedCount={selectedTickets.length}
						isPending={bulkUpdate.isPending}
						assigneeOptions={assigneeEditOptions}
						categoryOptions={categoryEditOptions}
						mailboxOptions={mailboxEditOptions}
						shortcutMenu={shortcutMenu}
						onShortcutMenuChange={setShortcutMenu}
						onApply={applyBulkUpdate}
						onClear={() => setSelectedIds(new Set())}
					/>
				)}
			</div>

			{(isSearching || !!filters.csmName) && (
				<SearchCriteriaBar
					searchValue={searchValue}
					filters={filters}
					onFiltersChange={onFiltersChange}
					categoryMap={categoryMap}
					teamMembers={teamMembers}
					accountId={accountId}
					accountName={accountName}
					csmOnly={!isSearching}
				/>
			)}
			{actionError && <div className={css(ts.actionError)}>{actionError}</div>}

			<div className={css(ts.tableScroll)}>
				<DataGrid
					aria-label='Support tickets'
					styles={[ts.fixedTable, showAccountColumn ? ts.fixedTableWithAccount : ts.fixedTableWithoutAccount]}
				>
					<colgroup>
						{columns.map(column => (
							<col key={column} className={css(getColumnStyle(column))} />
						))}
					</colgroup>
					<TicketTableHeader
						columns={columns}
						allSelected={allSelected}
						hasSelection={selectedTickets.length > 0}
						onToggleAll={toggleAll}
						filters={filters}
						onFiltersChange={onFiltersChange}
						categoryOptions={filterChoices.categoryOptions}
						assigneeOptions={filterChoices.assigneeOptions}
						isSearching={isSearching}
					/>
					<DataGridRows>
						{isLoading && stateRow(<LoadingSpinner type='small' />)}
						{!isLoading && isError && stateRow('Failed to load tickets.')}
						{!isLoading &&
							!isError &&
							tickets.length === 0 &&
							stateRow(isSearching ? 'No tickets match your search.' : 'No tickets in this view.')}
						{!isLoading &&
							!isError &&
							tickets.map(ticket => (
								<TicketTableRow
									key={ticket.id}
									ticket={ticket}
									columns={columns}
									category={ticket.categoryId ? categoryMap[ticket.categoryId] : undefined}
									assigneeName={getTicketAssigneeName(ticket, teamPresentation.agentNameMap)}
									selected={selectedIds.has(ticket.id)}
									onToggleSelect={() => toggleTicket(ticket.id)}
									assigneeOptions={assigneeEditOptions}
									categoryOptions={categoryEditOptions}
									agentNames={teamPresentation.agentNameMap}
									agentProfilePicMap={teamPresentation.agentProfilePicMap}
									agentProfilePicEmailMap={teamPresentation.agentProfilePicEmailMap}
									onActionError={setActionError}
									onOpen={() => onOpenTicket(ticket.id)}
								/>
							))}
					</DataGridRows>
				</DataGrid>
			</div>
			<TicketPagination page={page} totalPages={totalPages} onSelectPage={onSelectPage} />
		</section>
	);
};
